import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Settings, X, ShieldCheck, BarChart3, Megaphone } from 'lucide-react'
import { useCookieConsent } from '@/contexts/CookieConsentContext'
import { LocalizedLink } from './LocalizedLink'


/**
 * CookieConsent — Fenix Srl industrial cookie banner.
 *
 * Visual system:
 *  - White card surface, sharp rounded-sm corners, ink-700 body text.
 *  - A vertical brand-500 tick runs down the left edge of both banner and
 *    modal, mirroring the tick motif used on every primary CTA across the
 *    site (FinalCTA, Contacts "REC.01").
 *  - Tracked uppercase eyebrow "CONSENSO · REC.01" establishes rhythm with
 *    the rest of the pages and signals the engineered, record-like nature of
 *    the consent action.
 *  - Primary action: solid brand-blue with tick animation on hover.
 *  - Secondary action: white surface with brand-blue outline.
 *  - Preferences modal lists three categories; since Fenix currently loads
 *    neither Analytics nor Marketing scripts, those toggles are rendered as
 *    disabled rows with a clear "Non attivo" label and a diagonal brand
 *    stripe — honest, transparent, and easy to re-activate later.
 */
export function CookieConsent() {
  const { t } = useTranslation()
  const { showBanner, acceptAll, rejectAll, savePreferences } = useCookieConsent()
  const [showPreferences, setShowPreferences] = useState(false)

  // Analytics and Marketing have no scripts in this release — they are
  // rendered as permanently inactive in the UI, so the effective saved
  // preferences always match the "only necessary" baseline when the user
  // confirms from the preferences panel.
  const savedPrefs = {
    necessary: true,
    analytics: false,
    marketing: false,
  }

  if (!showBanner) return null

  return (
    <>
      {/* ================================================================
          BANNER — sticky bottom card
          ================================================================ */}
      {!showPreferences && (
        <div
          role="dialog"
          aria-labelledby="cookie-banner-title"
          aria-describedby="cookie-banner-message"
          className="fixed inset-x-3 bottom-3 md:inset-x-auto md:left-6 md:bottom-6 md:max-w-lg animate-in slide-in-from-bottom-5 fade-in duration-300"
          style={{ zIndex: 9999 }}
        >
          <div className="relative overflow-hidden rounded-sm bg-white shadow-[0_20px_50px_-20px_rgba(10,30,50,0.45)] ring-1 ring-ink-200">
            {/* Left brand tick */}
            <span
              aria-hidden="true"
              className="absolute left-0 top-0 bottom-0 w-1 bg-brand-500"
            />

            {/* Diagonal engineering accent in top-right corner */}
            <span
              aria-hidden="true"
              className="pointer-events-none absolute -right-6 -top-6 h-20 w-20 fx-diagonal-accent opacity-[0.08]"
            />

            <div className="relative pl-6 pr-5 py-5 md:pl-7 md:pr-6 md:py-6">
              {/* Eyebrow + close hint row */}
              <div className="mb-3 flex items-center gap-3">
                <span
                  aria-hidden="true"
                  className="block h-px w-8 bg-brand-500"
                />
                <p
                  id="cookie-banner-title"
                  className="text-[10px] font-bold uppercase tracking-[0.28em] text-brand-700"
                >
                  {t('cookie.eyebrow')}
                </p>
              </div>

              <p
                id="cookie-banner-message"
                className="text-sm leading-relaxed text-ink-700"
              >
                {t('cookie.message')}
              </p>

              {/* Action buttons — order: Reject · Accept all · Customize */}
              <div className="mt-5 flex flex-col-reverse sm:flex-row sm:items-center sm:gap-3">
                <button
                  type="button"
                  onClick={rejectAll}
                  className="mt-2 sm:mt-0 inline-flex items-center justify-center rounded-sm border border-ink-300 bg-white px-5 py-2.5 text-xs font-bold uppercase tracking-[0.1em] text-ink-700 transition-colors hover:border-ink-700 hover:text-ink-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400 focus-visible:ring-offset-2"
                >
                  {t('cookie.rejectAll')}
                </button>
                <button
                  type="button"
                  onClick={acceptAll}
                  className="group relative mt-2 sm:mt-0 inline-flex flex-1 items-center justify-center gap-2 rounded-sm bg-brand-500 px-5 py-2.5 text-xs font-bold uppercase tracking-[0.1em] text-white shadow-[0_10px_30px_-12px_rgba(74,155,220,0.55)] transition-[background-color,transform,box-shadow] duration-200 hover:bg-brand-400 hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-300 focus-visible:ring-offset-2"
                >
                  <span
                    aria-hidden="true"
                    className="absolute -left-[2px] top-1/2 block h-5 w-[2px] -translate-y-1/2 bg-white/90 transition-all duration-200 group-hover:h-7"
                  />
                  {t('cookie.acceptAll')}
                </button>
                <button
                  type="button"
                  onClick={() => setShowPreferences(true)}
                  className="mt-2 sm:mt-0 inline-flex items-center justify-center gap-1.5 rounded-sm border border-transparent px-4 py-2.5 text-xs font-bold uppercase tracking-[0.1em] text-ink-600 transition-colors hover:text-brand-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400 focus-visible:ring-offset-2"
                >
                  <Settings className="h-3.5 w-3.5" strokeWidth={2.5} />
                  {t('cookie.customize')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ================================================================
          PREFERENCES MODAL
          ================================================================ */}
      {showPreferences && (
        <div
          className="fixed inset-0 flex items-end md:items-center justify-center bg-ink-900/75 backdrop-blur-[2px] p-0 md:p-4 animate-in fade-in duration-200"
          style={{ zIndex: 10000 }}
          onClick={() => setShowPreferences(false)}
          role="dialog"
          aria-modal="true"
          aria-labelledby="cookie-prefs-title"
        >
          <div
            className="relative w-full md:max-w-xl bg-white rounded-t-sm md:rounded-sm shadow-[0_30px_80px_-20px_rgba(10,30,50,0.55)] max-h-[92vh] overflow-hidden flex flex-col animate-in slide-in-from-bottom-5 md:zoom-in-95 duration-200"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Left brand tick */}
            <span
              aria-hidden="true"
              className="absolute left-0 top-0 bottom-0 w-1 bg-brand-500"
            />

            {/* Header */}
            <div className="relative flex items-start justify-between gap-4 border-b border-ink-200 pl-7 pr-5 py-5">
              <div>
                <div className="mb-2 flex items-center gap-3">
                  <span
                    aria-hidden="true"
                    className="block h-px w-8 bg-brand-500"
                  />
                  <p className="text-[10px] font-bold uppercase tracking-[0.28em] text-brand-700">
                    {t('cookie.eyebrow')}
                  </p>
                </div>
                <h2
                  id="cookie-prefs-title"
                  className="text-xl md:text-2xl font-bold leading-tight text-ink-900"
                >
                  {t('cookie.preferencesTitle')}
                </h2>
              </div>
              <button
                type="button"
                onClick={() => setShowPreferences(false)}
                className="shrink-0 rounded-sm border border-transparent p-1.5 text-ink-500 transition-colors hover:bg-ink-100 hover:text-ink-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400"
                aria-label={t('aria.close', 'Chiudi')}
              >
                <X className="h-5 w-5" strokeWidth={2.25} />
              </button>
            </div>

            {/* Body */}
            <div className="relative flex-1 overflow-y-auto pl-7 pr-5 py-5">
              <p className="text-sm leading-relaxed text-ink-700 fx-text-pretty">
                {t('cookie.preferencesDescription')}
              </p>

              <div className="mt-5 space-y-3">
                {/* Necessary — always on */}
                <CategoryRow
                  icon={<ShieldCheck className="h-4 w-4" strokeWidth={2.25} />}
                  title={t('cookie.necessary')}
                  description={t('cookie.necessaryDesc')}
                  status="always-on"
                  statusLabel={t('cookie.alwaysOn')}
                />

                {/* Analytics — disabled (no scripts) */}
                <CategoryRow
                  icon={<BarChart3 className="h-4 w-4" strokeWidth={2.25} />}
                  title={t('cookie.analytics')}
                  description={t('cookie.analyticsDesc')}
                  status="inactive"
                  statusLabel={t('cookie.notActive')}
                  hint={t('cookie.categoryDisabledHint')}
                />

                {/* Marketing — disabled (no scripts) */}
                <CategoryRow
                  icon={<Megaphone className="h-4 w-4" strokeWidth={2.25} />}
                  title={t('cookie.marketing')}
                  description={t('cookie.marketingDesc')}
                  status="inactive"
                  statusLabel={t('cookie.notActive')}
                  hint={t('cookie.categoryDisabledHint')}
                />
              </div>

              <p className="mt-5 text-xs leading-relaxed text-ink-600">
                <LocalizedLink
                  to="privacy"
                  className="font-semibold text-brand-700 underline decoration-brand-500/40 decoration-1 underline-offset-[3px] transition-colors hover:text-brand-500 hover:decoration-brand-500"
                >
                  {t('cookie.learnMore')}
                </LocalizedLink>
                <span aria-hidden="true" className="mx-2 text-ink-300">·</span>
                <LocalizedLink
                  to="cookiePolicy"
                  className="font-semibold text-brand-700 underline decoration-brand-500/40 decoration-1 underline-offset-[3px] transition-colors hover:text-brand-500 hover:decoration-brand-500"
                >
                  {t('cookie.cookiePolicyLink')}
                </LocalizedLink>
              </p>
            </div>

            {/* Footer actions */}
            <div className="relative flex flex-col-reverse sm:flex-row sm:justify-end gap-2 sm:gap-3 border-t border-ink-200 pl-7 pr-5 py-4 bg-ink-50">
              <button
                type="button"
                onClick={() => setShowPreferences(false)}
                className="inline-flex items-center justify-center rounded-sm border border-ink-300 bg-white px-5 py-2.5 text-xs font-bold uppercase tracking-[0.1em] text-ink-700 transition-colors hover:border-ink-700 hover:text-ink-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400 focus-visible:ring-offset-2"
              >
                {t('cookie.cancel')}
              </button>
              <button
                type="button"
                onClick={() => {
                  savePreferences(savedPrefs)
                  setShowPreferences(false)
                }}
                className="group relative inline-flex items-center justify-center gap-2 rounded-sm bg-brand-500 px-5 py-2.5 text-xs font-bold uppercase tracking-[0.1em] text-white shadow-[0_10px_30px_-12px_rgba(74,155,220,0.55)] transition-[background-color,transform,box-shadow] duration-200 hover:bg-brand-400 hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-300 focus-visible:ring-offset-2"
              >
                <span
                  aria-hidden="true"
                  className="absolute -left-[2px] top-1/2 block h-5 w-[2px] -translate-y-1/2 bg-white/90 transition-all duration-200 group-hover:h-7"
                />
                {t('cookie.savePreferences')}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

/* ==================================================================
   CategoryRow — one category line in the preferences modal.
   ================================================================== */

type CategoryStatus = 'always-on' | 'inactive'

interface CategoryRowProps {
  icon: React.ReactNode
  title: string
  description: string
  status: CategoryStatus
  statusLabel: string
  hint?: string
}

function CategoryRow({
  icon,
  title,
  description,
  status,
  statusLabel,
  hint,
}: CategoryRowProps) {
  const isAlwaysOn = status === 'always-on'
  const isInactive = status === 'inactive'

  return (
    <div
      className={`relative rounded-sm border p-4 transition-colors ${
        isAlwaysOn
          ? 'border-brand-500/30 bg-brand-50/60'
          : 'border-ink-200 bg-white'
      }`}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span
              className={`inline-flex h-6 w-6 items-center justify-center rounded-sm ${
                isAlwaysOn
                  ? 'bg-brand-500 text-white'
                  : 'bg-ink-100 text-ink-500'
              }`}
            >
              {icon}
            </span>
            <p className="text-sm font-bold uppercase tracking-[0.08em] text-ink-900">
              {title}
            </p>
          </div>
          <p className="mt-2 text-xs leading-relaxed text-ink-600 fx-text-pretty">
            {description}
          </p>
          {hint && isInactive && (
            <p className="mt-2 inline-flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.18em] text-ink-500">
              <span
                aria-hidden="true"
                className="block h-px w-4 bg-ink-300"
              />
              {hint}
            </p>
          )}
        </div>

        {/* Status pill */}
        <div className="shrink-0">
          {isAlwaysOn ? (
            <span className="inline-flex items-center gap-1.5 rounded-sm bg-brand-500 px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.14em] text-white">
              <span
                aria-hidden="true"
                className="block h-1.5 w-1.5 rounded-full bg-white"
              />
              {statusLabel}
            </span>
          ) : (
            <span
              className="relative inline-flex items-center gap-1.5 overflow-hidden rounded-sm border border-ink-300 bg-white px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.14em] text-ink-500"
              aria-label={statusLabel}
            >
              <span
                aria-hidden="true"
                className="pointer-events-none absolute inset-0 opacity-40 fx-diagonal-accent"
                style={{ filter: 'grayscale(1)' }}
              />
              <span className="relative block h-1.5 w-1.5 rounded-full bg-ink-300" />
              <span className="relative">{statusLabel}</span>
            </span>
          )}
        </div>
      </div>
    </div>
  )
}
